<script setup lang="ts">
import type { CardImageOverlayProps } from '@/components/CardImageOverlay/CardImageOverlay.vue';

import { motion } from 'motion-v';

import { TabCardCarousel } from './index';

export interface TabContent {
	label: string;
	cards: CardImageOverlayProps[];
}

export interface TabCardContainerProps {
	tabs: TabContent[];
	/**
	 * Tab inicial selecionada (índice)
	 * @default 0
	 */
	initialTab?: number;
}

const props = withDefaults(defineProps<TabCardContainerProps>(), {
	initialTab: 0
});

const emit = defineEmits<{
	/**
	 * Emitido quando uma tab é selecionada
	 */
	tabChange: [tabIndex: number, tabLabel: string];
	/**
	 * Emitido quando um card é clicado
	 */
	cardClick: [tabIndex: number, cardIndex: number, event: MouseEvent];
}>();

const selectedTabIndex = ref(props.initialTab);

function handleTabClick(index: number, label: string): void {
	selectedTabIndex.value = index;
	emit('tabChange', index, label);
}

function handleCardClick(cardIndex: number, event: MouseEvent): void {
	emit('cardClick', selectedTabIndex.value, cardIndex, event);
}

const currentCards = computed(
	() => props.tabs[selectedTabIndex.value]?.cards || []
);
</script>

<template>
	<div
		class="relative w-full overflow-hidden bg-gradient-to-r from-[#1A1A1A] to-[#000000]"
	>
		<div
			class="mx-0 overflow-hidden px-5 !pr-0 pb-4 pt-6 md:px-5 md:py-6 md:pb-5 lg:p-11"
		>
			<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
			<div
				class="relative mb-6 h-[40px] w-full overflow-x-auto overflow-y-hidden border-b border-primary-light-4 [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [overscroll-behavior-x:contain] [overscroll-behavior-y:none] [scrollbar-width:none] lg:mb-8 lg:h-[55px] [&::-webkit-scrollbar]:hidden"
			>
				<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
				<motion.div
					class="absolute flex min-w-[496px] items-start gap-7"
					:initial="false"
				>
					<KeepAlive>
						<div
							v-for="(tab, index) in tabs"
							:key="tab.label"
							class="relative flex h-[39px] flex-col items-center self-stretch lg:h-[54px]"
						>
							<button
								type="button"
								:class="[
									index === selectedTabIndex
										? 'text-white'
										: 'text-white-dark-4 hover:text-white',
									'cursor-pointer'
								]"
								@click="handleTabClick(index, tab.label)"
							>
								<h3
									class="font-matter-sq text-xl font-normal tracking-[-0.32px] lg:text-2xl"
								>
									{{ tab.label }}
								</h3>
							</button>
							<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
							<motion.div
								v-if="index === selectedTabIndex"
								data-testid="tab-underline"
								layout-id="tab-card-underline"
								:initial="false"
								:transition="{
									type: 'spring',
									stiffness: 500,
									damping: 40,
									duration: 0.3
								}"
								class="absolute -bottom-px h-[2px] w-full bg-beige"
							/>
						</div>
					</KeepAlive>
				</motion.div>
			</div>

			<TabCardCarousel :cards="currentCards" @card-click="handleCardClick" />
		</div>
	</div>
</template>
